import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Link, SearchField } from '@biq/apex-ui';
import { Grid } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useAuth, useBlastAI, useZone } from '../../../core';
import { useBlastAIUserConversation } from '../../../hooks/mutateUserConversation';
import { validationsMessages } from '../../../lib';
import { CHATBOT } from '../ask-blast-helper';
import {
    blastAIChatDefaults,
    BlastAIChatInputs,
    blastAIChatSchema,
    blastChatStyles,
} from './blast-ai-chat-helper';
import { DataProtectionModal } from './DataProtectionModal';
import { Message } from './Message';
import { WelcomeMessage } from './WelcomeMessage';

export const BlastAIChat = (): React.JSX.Element => {
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const { getToken, getUserName } = useAuth();
    const { getSelectedDataZone } = useZone();
    const {
        handleNewUserMessage,
        clearConversation,
        blastConversation,
        isLoading,
    } = useBlastAI();
    const [userPrompt, setUserPrompt] = useState<string>('');
    const [displayDataProtectionModal, setDisplayDataProtectionModal] =
        useState<boolean>(false);
    const isPristine = blastConversation.conversation.length === 0;

    const { mutate: submitBlastAIConversationPrompt } =
        useBlastAIUserConversation();

    const {
        setError,
        formState: { errors },
        control,
        clearErrors,
        setValue,
    } = useForm<BlastAIChatInputs>({
        mode: 'all',
        resolver: yupResolver(blastAIChatSchema),
        defaultValues: blastAIChatDefaults,
    });

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
                chatContainerRef.current.scrollHeight;
        }
    }, [blastConversation.conversation]);

    const handleSubmitPrompt = (promptData: string): void => {
        handleNewUserMessage(
            promptData,
            getUserName(),
            getSelectedDataZone()?.sdz.toUpperCase()
        );
        setUserPrompt('');
        clearErrors('userPrompt');
        setValue('userPrompt', '');
        submitBlastAIConversationPrompt({
            token: getToken(),
            userPrompt: promptData,
            conversationId: blastConversation.conversationId,
        });
    };

    const submitPrompt = (): void => {
        if (!userPrompt || userPrompt.trim().length === 0) {
            setError('userPrompt', { type: 'required' });
            setUserPrompt('');
        } else {
            handleSubmitPrompt(userPrompt);
        }
    };

    const handleSearchField = (searchValue: string): void => {
        setUserPrompt(searchValue);
        clearErrors('userPrompt');
        setValue('userPrompt', searchValue);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            submitPrompt();
        }
    };

    const handleProtectDataClick = (): void => {
        setDisplayDataProtectionModal(true);
    };

    const handleDialogClose = (): void => {
        setDisplayDataProtectionModal(false);
    };

    const handleClearConversationClick = (): void => {
        clearConversation();
        clearErrors('userPrompt');
    };

    const handleQuestionPromptClick = (question: string): void => {
        setUserPrompt(question);
        setValue('userPrompt', question);
        handleSubmitPrompt(question);
    };

    return (
        <Grid sx={blastChatStyles.chatContainer}>
            {isPristine ? (
                <WelcomeMessage
                    userPrompt={userPrompt}
                    handleQuestionPromptClick={handleQuestionPromptClick}
                />
            ) : (
                <Grid
                    sx={blastChatStyles.chatContentContainer}
                    ref={chatContainerRef}
                >
                    {blastConversation.conversation.map((message, index) => (
                        <Message
                            key={`${message.type}_${index}`}
                            type={message.type}
                            value={message.value}
                            title={message.title}
                        />
                    ))}
                </Grid>
            )}
            <Grid sx={blastChatStyles.searchFieldContainer}>
                <Controller
                    name='userPrompt'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <SearchField
                            {...field}
                            fullWidth
                            variant='outlined'
                            value={userPrompt}
                            disabled={isLoading}
                            placeholder={CHATBOT.SEARCH_FIELD_PLACEHOLDER}
                            InputProps={{ startAdornment: null }}
                            inputProps={{
                                style: blastChatStyles.searchFieldInputField,
                            }}
                            onChange={(e): void => {
                                handleSearchField(e.target.value);
                            }}
                            onKeyDown={handleKeyPress}
                            error={!!errors.userPrompt}
                            errorText={
                                errors.userPrompt
                                    ? validationsMessages.required
                                    : ''
                            }
                        />
                    )}
                />
                <Grid sx={blastChatStyles.footerContainer}>
                    {isPristine ? (
                        <Grid sx={blastChatStyles.linkContainer}>
                            <Link
                                color='primary'
                                underline='always'
                                sx={blastChatStyles.conversationLink}
                                onClick={handleProtectDataClick}
                            >
                                {CHATBOT.DATA_PROTECTION_LINK_LABEL}
                            </Link>
                        </Grid>
                    ) : (
                        <Grid sx={blastChatStyles.linkContainer}>
                            <Link
                                color='primary'
                                underline='always'
                                sx={
                                    isLoading
                                        ? blastChatStyles.loadingConversationLink
                                        : blastChatStyles.conversationLink
                                }
                                onClick={handleClearConversationClick}
                            >
                                {CHATBOT.NEW_CONVERSATION_LINK_LABEL}
                            </Link>
                        </Grid>
                    )}
                    <Grid sx={blastChatStyles.buttonContainer}>
                        <Button
                            size='large'
                            color='primary'
                            variant='contained'
                            onClick={submitPrompt}
                            disabled={isLoading}
                        >
                            {CHATBOT.CHAT_BUTTON_LABEL}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            {displayDataProtectionModal && (
                <DataProtectionModal
                    open={displayDataProtectionModal}
                    onClose={handleDialogClose}
                />
            )}
        </Grid>
    );
};
